import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { blogApi } from "../api/blogApi";
import { BlogCategory } from "../types/Blog";
import {
  ArrowLeft,
  Save,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListChecks,
  Code,
  Quote,
  Minus,
  Link as LinkIcon,
  ImagePlus,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  Highlighter,
} from "lucide-react";

const categories: { value: BlogCategory; label: string }[] = [
  { value: "algorithm", label: "Algorithms" },
  { value: "system-design", label: "System Design" },
  { value: "behavioral", label: "Behavioral" },
  { value: "general", label: "General" },
];

const BlogEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [category, setCategory] = useState<BlogCategory>("general");
  const [coverImage, setCoverImage] = useState("");
  const [published, setPublished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({ placeholder: "Start writing your post..." }),
      Underline,
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false }),
      Image,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    editorProps: {
      attributes: {
        class: "prose prose-invert prose-lg max-w-none focus:outline-none min-h-[400px]",
      },
    },
  });

  useEffect(() => {
    if (id) {
      loadPost(id);
    }
  }, [id]);

  const loadPost = async (postId: string) => {
    try {
      const post = await blogApi.getById(postId);
      setTitle(post.title);
      setExcerpt(post.excerpt);
      setCategory(post.category);
      setCoverImage(post.coverImage || "");
      setPublished(post.published);
      if (editor && post.content) {
        editor.commands.setContent(post.content);
      }
    } catch (error) {
      console.error("Error loading post:", error);
    }
  };

  const handleSubmit = async (asPublished: boolean) => {
    if (!editor || !title.trim()) return;

    setIsSaving(true);
    try {
      const content = editor.getHTML();
      const postData = {
        title: title.trim(),
        content,
        excerpt: excerpt.trim() || title.trim(),
        category,
        author: "",
        coverImage: coverImage || undefined,
        published: asPublished,
      };

      if (id) {
        await blogApi.update(id, postData);
      } else {
        await blogApi.create(postData);
      }

      navigate("/admin/blog");
    } catch (error) {
      console.error("Error saving post:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const addLink = () => {
    if (!editor) return;
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    if (!editor) return;
    const url = window.prompt("Enter image URL:");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  if (!editor) return null;

  return (
    <>
      <Helmet>
        <title>{id ? "Edit" : "New"} Post - AlgoBuddy</title>
      </Helmet>

      <main className="min-h-screen bg-gray-950">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-gray-950/80 backdrop-blur-lg border-b border-gray-800/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
            <button
              onClick={() => navigate("/admin/blog")}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Admin
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSubmit(false)}
                disabled={isSaving || !title.trim()}
                className="px-3 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-800 hover:border-gray-700 rounded-lg disabled:opacity-30 transition-colors"
              >
                Save Draft
              </button>
              <button
                onClick={() => handleSubmit(true)}
                disabled={isSaving || !title.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white text-gray-950 rounded-lg hover:bg-gray-200 disabled:opacity-30 transition-colors"
              >
                <Save className="w-3.5 h-3.5" />
                {isSaving ? "Publishing..." : "Publish"}
              </button>
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Cover image */}
          <div className="mb-8">
            {coverImage ? (
              <div className="relative">
                <img
                  src={coverImage}
                  alt="Cover"
                  className="w-full h-48 sm:h-64 object-cover rounded-xl"
                />
                <button
                  onClick={() => setCoverImage("")}
                  className="absolute top-3 right-3 px-2 py-1 text-xs bg-gray-950/80 text-gray-400 hover:text-white rounded-lg transition-colors"
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  const url = window.prompt("Enter cover image URL:");
                  if (url) setCoverImage(url);
                }}
                className="w-full h-32 border-2 border-dashed border-gray-800 rounded-xl flex items-center justify-center gap-2 text-gray-600 hover:text-gray-400 hover:border-gray-700 transition-colors"
              >
                <ImagePlus className="w-5 h-5" />
                Add cover image
              </button>
            )}
          </div>

          {/* Title */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post title"
            className="w-full text-4xl sm:text-5xl font-bold text-white bg-transparent border-none outline-none placeholder-gray-700 mb-4 tracking-tight"
          />

          {/* Excerpt */}
          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Write a short excerpt..."
            rows={2}
            className="w-full text-lg text-gray-400 bg-transparent border-none outline-none placeholder-gray-700 mb-6 resize-none"
          />

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-3 mb-8 pb-8 border-b border-gray-800/60">
            {/* Category */}
            <div className="flex items-center gap-1.5">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setCategory(cat.value)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                    category === cat.value
                      ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                      : "bg-gray-800/50 text-gray-500 border-gray-800 hover:text-gray-300 hover:border-gray-700"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <div className="w-px h-5 bg-gray-800" />

            {/* Published toggle */}
            <button
              onClick={() => setPublished(!published)}
              className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                published
                  ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                  : "bg-gray-800/50 text-gray-500 border-gray-800"
              }`}
            >
              {published ? "Published" : "Draft"}
            </button>
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-0.5 mb-6 p-2 bg-gray-900 border border-gray-800 rounded-xl sticky top-14 z-10">
            <ToolbarBtn
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              title="Undo"
            >
              <Undo className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              title="Redo"
            >
              <Redo className="w-4 h-4" />
            </ToolbarBtn>

            <div className="w-px h-5 bg-gray-800 mx-1" />

            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor.isActive("bold")}
              title="Bold"
            >
              <Bold className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor.isActive("italic")}
              title="Italic"
            >
              <Italic className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              active={editor.isActive("underline")}
              title="Underline"
            >
              <UnderlineIcon className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleStrike().run()}
              active={editor.isActive("strike")}
              title="Strikethrough"
            >
              <Strikethrough className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleHighlight({ color: "#fbbf24" }).run()
              }
              active={editor.isActive("highlight")}
              title="Highlight"
            >
              <Highlighter className="w-4 h-4" />
            </ToolbarBtn>

            <div className="w-px h-5 bg-gray-800 mx-1" />

            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }
              active={editor.isActive("heading", { level: 1 })}
              title="Heading 1"
            >
              <Heading1 className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
              active={editor.isActive("heading", { level: 2 })}
              title="Heading 2"
            >
              <Heading2 className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 3 }).run()
              }
              active={editor.isActive("heading", { level: 3 })}
              title="Heading 3"
            >
              <Heading3 className="w-4 h-4" />
            </ToolbarBtn>

            <div className="w-px h-5 bg-gray-800 mx-1" />

            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleBulletList().run()
              }
              active={editor.isActive("bulletList")}
              title="Bullet List"
            >
              <List className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleOrderedList().run()
              }
              active={editor.isActive("orderedList")}
              title="Ordered List"
            >
              <ListOrdered className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleTaskList().run()
              }
              active={editor.isActive("taskList")}
              title="Task List"
            >
              <ListChecks className="w-4 h-4" />
            </ToolbarBtn>

            <div className="w-px h-5 bg-gray-800 mx-1" />

            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleCodeBlock().run()
              }
              active={editor.isActive("codeBlock")}
              title="Code Block"
            >
              <Code className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().toggleBlockquote().run()
              }
              active={editor.isActive("blockquote")}
              title="Quote"
            >
              <Quote className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().setHorizontalRule().run()
              }
              title="Divider"
            >
              <Minus className="w-4 h-4" />
            </ToolbarBtn>

            <div className="w-px h-5 bg-gray-800 mx-1" />

            <ToolbarBtn onClick={addLink} title="Add Link">
              <LinkIcon className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn onClick={addImage} title="Add Image">
              <ImagePlus className="w-4 h-4" />
            </ToolbarBtn>

            <div className="w-px h-5 bg-gray-800 mx-1" />

            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().setTextAlign("left").run()
              }
              active={editor.isActive({ textAlign: "left" })}
              title="Align Left"
            >
              <AlignLeft className="w-4 h-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() =>
                editor.chain().focus().setTextAlign("center").run()
              }
              active={editor.isActive({ textAlign: "center" })}
              title="Align Center"
            >
              <AlignCenter className="w-4 h-4" />
            </ToolbarBtn>
          </div>

          {/* Editor content */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 sm:p-10">
            <EditorContent editor={editor} />
          </div>

          {/* Bottom actions */}
          <div className="mt-10 flex items-center justify-between">
            <button
              onClick={() => navigate("/admin/blog")}
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSubmit(false)}
                disabled={isSaving || !title.trim()}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-800 hover:border-gray-700 rounded-lg disabled:opacity-30 transition-colors"
              >
                Save Draft
              </button>
              <button
                onClick={() => handleSubmit(true)}
                disabled={isSaving || !title.trim()}
                className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium bg-white text-gray-950 rounded-xl hover:bg-gray-200 disabled:opacity-30 transition-colors"
              >
                {isSaving ? "Publishing..." : "Publish"}
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

// Toolbar button component
const ToolbarBtn: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded-lg transition-colors ${
      active
        ? "bg-gray-700 text-white"
        : "text-gray-500 hover:text-white hover:bg-gray-800"
    } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
  >
    {children}
  </button>
);

export default BlogEditorPage;
